import Button, { Size } from 'components/Button';
import Icon from 'components/Icon';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatAmt, pluralize, readAxiosErr } from 'utils/misc';
import clx from 'classnames';
import useAuth from 'hooks/useAuth';
import useModal from 'hooks/useModal';
import BottomSheet from 'components/BottomSheet';
import CreateEstimate from 'components/CreateEstimate';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { parseEpochAndFormat } from 'utils/time';
import { removeSumoquote } from 'queries/estimates';
import toast from 'react-hot-toast';
import Confirmation from 'components/Confirmation';
import Popover from 'components/Popover';
import { MAX_ESTIMATES } from 'utils/constants';
import { setEstimate } from 'queries/job';
import usePageLoader from 'hooks/usePageLoader';
import { truncate } from 'lodash';

type AppProps = {
  job: Record<string, any>;
};

const EstimateBox: React.FC<AppProps> = ({ job }) => {
  // const [estimateObj, setEstimateObj] = useState(job.estimate);
  const estimateObj = job.estimate;
  const navigate = useNavigate();
  const { userInfo } = useAuth();
  const [
    createEstimateModal,
    showCreateEstimateModal,
    closeCreateEstimateModal,
  ] = useModal();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [confirmOpen, showConfirmModal, closeConfirmModal] = useModal();
  const { startLoader, stopLoader } = usePageLoader();

  const removeSumoquoteMutation = useMutation(() => removeSumoquote(job.id), {
    onError: (err: any) => {
      toast.error(readAxiosErr(err));
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries(['job-checklist', job.id?.toString()]),
        queryClient.invalidateQueries(['job-detail', job.id?.toString()]),
      ]);
    },
  });

  const setEstimateMutation = useMutation(
    (estId: string) => setEstimate(job.id, { estimate: estId }),
    {
      onMutate: () => {
        startLoader();
      },
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries(['job-detail', job.id?.toString()]),
          queryClient.invalidateQueries(['job-stats', job.id?.toString()]),
        ]);

        setTimeout(() => {
          queryClient.invalidateQueries(['estimates']);
          queryClient.invalidateQueries(['job-checklist', job.id?.toString()]);
          queryClient.invalidateQueries(['expenses']);
          queryClient.invalidateQueries(['matorders']);
          queryClient.invalidateQueries(['workorders']);
          queryClient.invalidateQueries(['payments']);
        }, 1000);

        // setEstimateObj(est);
      },
      onError: (err: any) => {
        toast.error(readAxiosErr(err));
      },
      onSettled: () => {
        stopLoader();
      },
    }
  );

  const hide_profit = userInfo?.controls?.hide_profit;
  const multi_estimates = userInfo?.controls?.can_use_multiple_estimates;
  const primary_estimate = job?.primary_estimate;
  const date_signed = job?.date_signed;

  const show_estimate_selector = multi_estimates && !primary_estimate;

  const estimate = useMemo(() => {
    const options = [
      {
        id: 'contract',
        label: 'Contract',
        value: estimateObj?.gt_price,
      },
      {
        id: 'payments',
        label: 'Payments',
        value: estimateObj?.payments,
      },
      {
        id: 'balance',
        label: 'Balance',
        value: estimateObj?.balance,
        style: { color: 'red' },
      },
    ];

    if (!hide_profit) {
      options.push({
        id: 'profit',
        label: 'Profit',
        value: estimateObj?.profit,
        style: { color: 'green' },
      });
    }

    return options;
  }, [hide_profit, estimateObj]);

  if (job.sumoquote_meta?.status === 'processing') {
    return (
      <div className="px-3 py-2 bg-white shadow-md rounded-lg">
        <div className="v-center cursor-pointer relative">
          <Icon name="calculator" size={18} className="text-primary" />{' '}
          <span className="pl-1 title">Estimate</span>
          <div className="absolute -right-2 top-0">
            <Icon name="sumoquote" size={14} className="w-8 animate-pulse" />
          </div>
        </div>
        <div className="w-[152px] mt-4">
          <div className="text-xs text-text-medium relative">
            Waiting for sumoquote...
          </div>
          <div className="mt-2">
            <div className="key">Order placed at:</div>
            <div className="text-xs font-bold">
              {parseEpochAndFormat(job.sumoquote_meta?.order_placed_timestamp)}
            </div>
          </div>
        </div>
        <div className="apart mt-8">
          <div
            className="v-center space-x-[2px] cursor-pointer"
            onClick={async () => {
              setLoading(true);
              await Promise.all([
                queryClient.invalidateQueries([
                  'job-checklist',
                  job.id?.toString(),
                ]),
                queryClient.invalidateQueries([
                  'job-detail',
                  job.id?.toString(),
                ]),
              ]);
              setLoading(false);
            }}
          >
            <Icon
              name="refresh"
              size={18}
              className={clx('text-primary', {
                'animate-spin': loading,
              })}
            />
            <span className="text-xs">Refresh</span>
          </div>
          {job.sumoquote_meta?.is_delayed && (
            <div
              className="v-center space-x-[2px] cursor-pointer"
              onClick={showConfirmModal}
            >
              <Icon name="delete" size={16} className="text-error" />
              <span className="text-xs text-error">Delete</span>
            </div>
          )}
        </div>
        {confirmOpen && (
          <Confirmation
            title="Remove Sumoquote?"
            message="This action will remove sumoquote link for this job. You can always place the order with sumoquote later."
            open={confirmOpen}
            closeConfirm={closeConfirmModal}
            onClick={() => removeSumoquoteMutation.mutate()}
            isLoading={removeSumoquoteMutation.isLoading}
          />
        )}
      </div>
    );
  }

  const heading = (
    <div className="apart">
      <div className="v-center cursor-pointer">
        <Icon name="calculator" size={18} className="text-primary" />{' '}
        <span className="pl-1 title">
          {pluralize(job.estimates?.length, 'Estimate')}
          {multi_estimates && `(${job.estimates?.length})`}
        </span>
        {show_estimate_selector && (
          <Icon
            name="chevron-down"
            className="ml-1 relative top-[2px]"
            size={16}
          />
        )}
      </div>
    </div>
  );

  const createEstimateIcon = (
    <Icon
      name="plus"
      size={20}
      className="text-primary relative top-[1px]"
      onClick={() => {
        if (job.estimates?.length < MAX_ESTIMATES) {
          showCreateEstimateModal();
        }
      }}
    />
  );

  return (
    <div className="px-2 py-2 bg-white shadow-md rounded-lg">
      <div className="w-full relative">
        {show_estimate_selector ? (
          <Popover trigger={heading} panelClassName="!-right-16 min-w-[180px]">
            <div className="flex flex-col">
              {job.estimates?.map((est: any) => (
                <div
                  key={est.id}
                  className={clx('px-4 py-1 cursor-pointer', {
                    'bg-primary-negative text-primary':
                      estimateObj?.id === est.id,
                  })}
                  onClick={() => setEstimateMutation.mutate(est.id)}
                >
                  {truncate(est.name, { length: 24 })}
                </div>
              ))}
              <div className="mt-4 flex justify-center">
                <Button
                  label="Create New Estimate"
                  disabled={job.estimates?.length >= MAX_ESTIMATES}
                  size={Size.Small}
                  leftIcon="plus"
                  onClick={() => {
                    showCreateEstimateModal();
                  }}
                />
              </div>
            </div>
          </Popover>
        ) : (
          heading
        )}

        {multi_estimates && !date_signed && (
          <div className="absolute -right-[2px] top-[2px]">
            {job.estimates?.length >= MAX_ESTIMATES ? (
              <Popover
                panelClassName="!-right-16 min-w-[200px]"
                trigger={createEstimateIcon}
              >
                <div>
                  <div className="text-base font-medium">
                    Estimate Limit Reached
                  </div>
                  <div className="text-sm text-text-light">
                    You&apos;ve reached the maximum number of estimates (
                    {MAX_ESTIMATES}). Delete an existing one to create more.
                  </div>
                </div>
              </Popover>
            ) : (
              createEstimateIcon
            )}
          </div>
        )}

        {job.estimate ? (
          <div className="mt-2">
            {estimate.map((est) => (
              <div key={est.id} className="v-center space-y-1">
                <div className="w-[78px] key">{est.label}:</div>{' '}
                <div className="value" style={est.style}>
                  {formatAmt(est.value)}
                </div>
              </div>
            ))}
            <div
              className={clx('cursor-pointer v-center text-xs text-primary', {
                'mt-8': hide_profit,
                'mt-2': !hide_profit,
              })}
              onClick={() => {
                if (job.estimate) {
                  if (multi_estimates) {
                    return navigate(`/jobs/${job.id}/estimates/`);
                  } else {
                    return navigate(
                      `/jobs/${job.id}/estimates/${job.estimate.id}`
                    );
                  }
                }
                return showCreateEstimateModal();
              }}
            >
              View {multi_estimates ? 'all estimates' : 'estimate'}{' '}
              <Icon name="arrow-right" className="ml-1" size={16} />
            </div>
          </div>
        ) : (
          <div>
            <div className="w-[152px] mt-12">
              <div className="text-sm mb-2 text-text-light">No Estimate</div>
              <Button
                label="Create Estimate"
                size={Size.Medium}
                leftIcon="plus"
                className="mt-2 relative right-1"
                onClick={(e) => {
                  e.preventDefault();
                  if (job.estimates?.length < MAX_ESTIMATES) {
                    showCreateEstimateModal();
                  }
                }}
              />
            </div>
          </div>
        )}
      </div>
      <BottomSheet
        open={createEstimateModal}
        closeSheet={closeCreateEstimateModal}
        maxWidth="max-w-2xl"
      >
        <CreateEstimate jobId={job.id} closeModal={closeCreateEstimateModal} />
      </BottomSheet>
    </div>
  );
};

export default EstimateBox;
